
import { Component, Vue, Ref, Watch } from 'vue-property-decorator';
import PurchaseOrderDetailModal from './components/PurchaseOrderDetailModal.vue';
import PurchaseOrderCreateModal from './components/PurchaseOrderCreateModal.vue';
import SelectDistributorModal from './components/SelectDistributorModal.vue';
import ConfirmReceiptModal from './components/ConfirmReceiptModal.vue';
import SignReceiptModal from './components/SignReceiptModal.vue';
import lazyInject from '@/di';
import SupplyService from '@/services/supply.service';
import { CPurchaseOrderList } from '@/models/supply.model';
import moment from 'moment';
import { AuthEnum } from '@/services/permission.service';

@Component({
  components: {
    PurchaseOrderDetailModal,
    PurchaseOrderCreateModal,
    SelectDistributorModal,
    ConfirmReceiptModal,
    SignReceiptModal,
  },
})
export default class PurchaseOrder extends Vue {
  loading = false;

  AuthEnum = AuthEnum;

  @Ref('purchaseOrderDetailModal')
  purchaseOrderDetailModal!: PurchaseOrderDetailModal;

  @Ref('purchaseOrderCreateModal')
  purchaseOrderCreateModal!: PurchaseOrderCreateModal;

  @Ref('selectDistributorModal')
  selectDistributorModal!: SelectDistributorModal;

  @Ref('confirmReceiptModal')
  confirmReceiptModal!: ConfirmReceiptModal;

  @Ref('signReceiptModal')
  signReceiptModal!: SignReceiptModal;

  @lazyInject(SupplyService)
  supplyService!: SupplyService;

  dataSource: any[] = [];

  filterForm: any = {
    order_no: '',
    distributor_code: '',
    start_date: '',
    end_date: '',
    pay_status: undefined,
    shipment_status: undefined,
    invoice_status: undefined,
  };

  distributorOptions: any = [];

  // 0-未支付，1-已支付(待收款)，2-已确认收款，3-已取消
  payStatusOptions = [
    { label: '待付款', value: 0 },
    { label: '待收款', value: 1 },
    { label: '已收款', value: 2 },
    { label: '已取消', value: 3 },
  ];

  // 0-待发货，1-在途，2-已签收
  shipmentStatusOptions = [
    { label: '待发货', value: 0 },
    { label: '已发货', value: 1 },
    { label: '已签收', value: 2 },
  ];

  // 0-未开票，2-已开票
  invoiceStatusOptions = [
    { label: '未开票', value: 0 },
    { label: '已开票', value: 1 },
  ];

  paymentStatusMap: any = {
    0: '未付款',
    1: '已付款',
  };

  logisticsStatusMap: any = {
    0: '未发货',
    1: '已发货',
  };

  invoiceStatusMap: any = {
    0: '未开票',
    1: '已开票',
  };

  orderStatusMap: any = {
    0: '待付款',
    1: '待发货',
    2: '已完成',
  };

  columns = [
    {
      title: '订单编号',
      dataIndex: 'orderNo',
      align: 'center',
    },
    {
      title: '经销商',
      dataIndex: 'companyName',
      align: 'center',
    },
    {
      title: '省份',
      dataIndex: 'province',
      align: 'center',
    },
    {
      title: '壶数',
      dataIndex: 'quantity',
      align: 'center',
    },
    {
      title: '订单状态',
      dataIndex: 'orderStatus',
      align: 'center',
      scopedSlots: { customRender: 'orderStatus' },
    },
    {
      title: '付款状态',
      dataIndex: 'payStatus',
      align: 'center',
      scopedSlots: { customRender: 'payStatus' },
    },
    {
      title: '物流状态',
      dataIndex: 'shipmentStatus',
      align: 'center',
      scopedSlots: { customRender: 'shipmentStatus' },
    },
    {
      title: '开票状态',
      dataIndex: 'invoiceStatus',
      align: 'center',
      scopedSlots: { customRender: 'invoiceStatus' },
    },
    {
      title: '收货凭证',
      dataIndex: 'receiveVoucherStatus',
      align: 'center',
      scopedSlots: { customRender: 'receiveVoucherStatus' },
    },
    {
      title: '下单时间',
      dataIndex: 'createdAt',
      align: 'center',
    },
    {
      title: '操作',
      dataIndex: 'action',
      align: 'center',
      fixed: 'right',
      width: 200,
      scopedSlots: { customRender: 'action' },
    },
  ];

  receiveVoucherStatusMap: any = {
    0: '未上传',
    1: '已上传',
  };

  pagination: any = {
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: this.$store.state.pageSize.pageSize,
    showTotal: () => `共${this.pagination.total}条`,
    onChange: this.paginationChange,
    onShowSizeChange: this.onShowSizeChange,
  };

  @Watch('$route')
  async handleSearch() {
    const {
      order_no,
      distributor_code,
      start_date,
      end_date,
      pay_status,
      shipment_status,
      invoice_status,
      page,
      pageSize,
    } = this.$route.query;
    const params: any = { page: page || 1, pageSize: pageSize || 10 };
    if (order_no) {
      params.order_no = order_no;
      this.filterForm.order_no = order_no;
    }
    if (distributor_code) {
      params.distributor_id = distributor_code;
      this.filterForm.distributor_code = +distributor_code;
    }
    if (start_date && end_date) {
      params.start_date = start_date;
      params.end_date = end_date;
      this.filterForm.start_date = start_date;
      this.filterForm.end_date = end_date;
    }
    if (pay_status !== undefined) {
      params.pay_status = pay_status;
      this.filterForm.pay_status = +pay_status;
    }
    if (shipment_status !== undefined) {
      params.shipment_status = shipment_status;
      this.filterForm.shipment_status = +shipment_status;
    }
    if (invoice_status !== undefined) {
      params.invoice_status = invoice_status;
      this.filterForm.invoice_status = +invoice_status;
    }
    this.getList(params);
  }

  async loadDistributorOptions(companyName = '') {
    const res: any = await this.supplyService.getDistributorSearch({
      company_name: companyName,
    });
    const list = res || [];
    this.distributorOptions = list.map((item: any) => ({
      label: item.name,
      value: item.id,
    }));
  }

  onDistributorSearch(companyName: string) {
    this.loadDistributorOptions(companyName);
  }

  getQueryParams() {
    const {
      order_no,
      distributor_code,
      start_date,
      end_date,
      pay_status,
      shipment_status,
      invoice_status,
    } = this.filterForm;
    const { current, pageSize } = this.pagination;
    const query: any = { page: current, pageSize };
    if (order_no) {
      query.order_no = order_no;
    }
    if (distributor_code) {
      query.distributor_code = distributor_code;
    }
    if (start_date && end_date) {
      query.start_date = start_date;
      query.end_date = end_date;
    }
    if (pay_status !== undefined) {
      query.pay_status = pay_status;
    }
    if (shipment_status !== undefined) {
      query.shipment_status = shipment_status;
    }
    if (invoice_status !== undefined) {
      query.invoice_status = invoice_status;
    }
    query.timestamp = Math.round(Date.now() / 1000);
    this.$router.push({
      query,
    });
  }

  search() {
    this.pagination.current = 1;
    this.getQueryParams();
  }

  mounted() {
    this.handleSearch();
    this.loadDistributorOptions();
  }

  async getList(params: any) {
    const res = await this.supplyService.getDistributorPurchaseOrderList(
      params,
      this.$el,
    );
    this.loading = false;
    this.dataSource = res.list.map(
      (item: any) => CPurchaseOrderList.from(item),
      // eslint-disable-next-line function-paren-newline
    );
    this.pagination.total = res.total;
    this.pagination.current = res.page;
    this.pagination.pageSize = res.pageSize;
  }

  paginationChange(page: number, pageSize: number) {
    this.pagination.current = page;
    this.pagination.pageSize = pageSize;
    this.getQueryParams();
  }

  onShowSizeChange(current: number, size: number) {
    this.pagination.current = 1;
    this.pagination.pageSize = size;
    this.getQueryParams();
  }

  reset() {
    this.filterForm = {
      order_no: '',
      distributor_code: '',
      start_date: '',
      end_date: '',
      pay_status: undefined,
      shipment_status: undefined,
      invoice_status: undefined,
    };
    this.pagination.current = 1;
    this.getQueryParams();
  }

  formatDate(date: any) {
    return date ? moment(date).format('YYYY-MM-DD') : '';
  }

  onChangeOrderDate(dates: any) {
    this.filterForm.start_date = this.formatDate(dates && dates[0]);
    this.filterForm.end_date = this.formatDate(dates && dates[1]);
  }

  viewDetail(record: any) {
    this.purchaseOrderDetailModal.open(record);
  }

  confirmReceipt(record: any) {
    this.confirmReceiptModal.open(record);
  }

  signReceipt(record: any) {
    this.signReceiptModal.open(record);
  }

  openCreateModal() {
    this.selectDistributorModal.open();
  }

  onDistributorSelect(distributor: any) {
    this.purchaseOrderCreateModal.open(distributor);
  }
}
